import './BatterSingleSessionContactPointChart.scss';

import {
  BatterSingleSessionContactPointChartConfigDto,
  BatterSingleSessionContactPointChartDataDto,
  BatterSingleSessionContactPointGroup,
} from 'types/CodegenReportTypes';

import StrikeZone, {
  generateRandomStrikeZonePoints,
  getRelativeStrikeZonePointX,
  getRelativeStrikeZonePointY,
} from 'components/StrikeZone/StrikeZone';
import { ChartProps } from 'utilities/chartIdToComponent';
import { useCallback, useMemo } from 'react';
import { useChartDataDisplay } from 'hooks/helperHooks';
import Legend, { LegendItem } from 'components/Legend/Legend';
import { pitchCallToColor, pitchCallToTextColor, playResultToColor, playResultToTextColor } from 'utilities/colorHelpers';
import { Tooltip, Typography } from '@trackman/web-shared-components';
import { mergeFoulBallVariants, playResults } from 'utilities/constants';
import { ReactComponent as BasePlateTop } from 'svg/BasePlateTop.svg';

function getGroupLabelColor(groupLabel: string) {
  return pitchCallToColor[groupLabel] ?? playResultToColor[groupLabel];
}

function getGroupLabelTextColor(groupLabel: string) {
  return pitchCallToTextColor[groupLabel] ?? playResultToTextColor[groupLabel];
}

const strikeZoneTopViewRangeX = [-8.5, 8.5];
const strikeZoneTopViewRangeY = [0, 42.5];

function getRelativeStrikeZoneTopViewPointX(x: number): number {
  return ((x - strikeZoneTopViewRangeX[0]) / (strikeZoneTopViewRangeX[1] - strikeZoneTopViewRangeX[0])) * 100;
}

function getRelativeStrikeZoneTopViewPointY(y: number): number {
  return ((y - strikeZoneTopViewRangeY[0]) / (strikeZoneTopViewRangeY[1] - strikeZoneTopViewRangeY[0])) * 100;
}

function generateRandomStrikeZoneTopViewPoints(amount: number): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i < amount; i++) {
    points.push({
      x: Math.random() * (strikeZoneTopViewRangeX[1] - strikeZoneTopViewRangeX[0]) + strikeZoneTopViewRangeX[0],
      y: Math.random() * (strikeZoneTopViewRangeY[1] - strikeZoneTopViewRangeY[0]) + strikeZoneTopViewRangeY[0],
    });
  }
  return points;
}

function getRandomPlayResult(): string {
  return playResults[Math.floor(Math.random() * playResults.length)];
}

const BatterSingleSessionContactPointChart = ({
  chartData,
  isLoading,
  extraChartData,
}: ChartProps<BatterSingleSessionContactPointChartDataDto, BatterSingleSessionContactPointChartConfigDto>) => {
  const simulateChartData = useCallback((isLoading: boolean): BatterSingleSessionContactPointGroup[] => {
    const groupLabels = ['InPlay'];

    return isLoading
      ? groupLabels.map((label, i) => ({
          pitchCall: label,
          batterViewPoints: generateRandomStrikeZonePoints(7).map(({ x, y }, j) => ({
            x,
            y,
            pitchOfGame: i * 7 + j + 1,
            playResult: getRandomPlayResult(),
          })),
          topViewPoints: generateRandomStrikeZoneTopViewPoints(7).map(({ x, y }, j) => ({
            x,
            y,
            pitchOfGame: i * 7 + j + 1,
            playResult: getRandomPlayResult(),
          })),
        }))
      : [];
  }, []);

  const chartDataDisplay = useChartDataDisplay(chartData?.groups, simulateChartData, isLoading);

  const legendItemLabels = useMemo(
    () => [
      ...new Set(chartDataDisplay.map((group) => mergeFoulBallVariants(group.pitchCall)).filter((label) => label !== 'InPlay')),
      ...new Set(
        chartDataDisplay
          .map((group) => [...group.batterViewPoints, ...group.topViewPoints].map((x) => x.playResult).filter((x) => x !== 'Undefined'))
          .flat()
      ),
    ],
    [chartDataDisplay]
  );

  const legendItems: LegendItem[] = useMemo(
    () =>
      legendItemLabels.map((groupLabel) => ({
        label: groupLabel,
        dotProps: { color: getGroupLabelColor(groupLabel) },
      })),
    [legendItemLabels]
  );

  return (
    <div className='BatterSingleSessionContactPointChart-container'>
      <div className='BatterSingleSessionContactPointChart-batter-view'>
        <StrikeZone basePlateOrientation={'BatterSide'} showStrikeZoneGrid={true}>
          {chartDataDisplay.map((group) =>
            group.batterViewPoints.map((point) => (
              <Tooltip key={`batterview-point-${point.pitchOfGame}`} title={`(${point.x.toFixed(2)}, ${point.y.toFixed(2)})`}>
                <div
                  className='StrikeZone-point'
                  style={{
                    left: `${getRelativeStrikeZonePointX(point.x * -1)}%`,
                    bottom: `${getRelativeStrikeZonePointY(point.y)}%`,
                    backgroundColor: getGroupLabelColor(group.pitchCall === 'InPlay' ? point.playResult : group.pitchCall) + '80',
                    borderColor: getGroupLabelColor(group.pitchCall === 'InPlay' ? point.playResult : group.pitchCall),
                    display: 'grid',
                    placeItems: 'center',
                    padding: 1,
                  }}
                >
                  <Typography
                    variant={'label'}
                    style={{
                      color: getGroupLabelTextColor(group.pitchCall === 'InPlay' ? point.playResult : group.pitchCall),
                      lineHeight: 0,
                    }}
                  >
                    {point.pitchOfGame}
                  </Typography>
                </div>
              </Tooltip>
            ))
          )}
        </StrikeZone>
        <Legend items={legendItems} />
      </div>
      <div className='BatterSingleSessionContactPointChart-top-view'>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: '-35%', width: '2px', backgroundColor: '#a0a0a0' }}></div>
        <div style={{ position: 'absolute', top: 0, bottom: 0, right: '-35%', width: '2px', backgroundColor: '#a0a0a0' }}></div>
        {chartDataDisplay.map((group) =>
          group.topViewPoints.map((point) => (
            <Tooltip key={`topview-point-${point.pitchOfGame}`} title={`(${point.x.toFixed(2)}, ${point.y.toFixed(2)})`}>
              <div
                className='StrikeZone-point'
                style={{
                  height: '6.5%',
                  left: `${getRelativeStrikeZoneTopViewPointX(point.x * -1)}%`,
                  bottom: `${getRelativeStrikeZoneTopViewPointY(point.y)}%`,
                  backgroundColor: getGroupLabelColor(group.pitchCall === 'InPlay' ? point.playResult : group.pitchCall) + '80',
                  borderColor: getGroupLabelColor(group.pitchCall === 'InPlay' ? point.playResult : group.pitchCall),
                  display: 'grid',
                  placeItems: 'center',
                  padding: 1,
                }}
              >
                <Typography
                  variant={'label'}
                  style={{
                    color: getGroupLabelTextColor(group.pitchCall === 'InPlay' ? point.playResult : group.pitchCall),
                    lineHeight: 0,
                  }}
                >
                  {point.pitchOfGame}
                </Typography>
              </div>
            </Tooltip>
          ))
        )}
        <BasePlateTop className='BatterSingleSessionContactPointChart-base-plate-top' />
      </div>
    </div>
  );
};

export default BatterSingleSessionContactPointChart;
